import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import AuditTrail from '../components/AuditTrail';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Building2,
  Check,
  ChevronDown,
  Eye,
  Link as LinkIcon,
  MinusCircle,
  MoreHorizontal,
  Pencil,
  Plus,
  PlusCircle,
  Trash2,
  UserRound,
  X,
} from 'lucide-react';

const terminalStatuses = new Set(['verified', 'rejected', 'revoked', 'expired']);
const badgeStyles = {
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  verified: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  needs_review: 'bg-amber-50 text-amber-800 border-amber-300',
  needs_action: 'bg-amber-50 text-amber-800 border-amber-300',
  pending_verification: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  pending: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  opened: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  draft: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  created: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  not_received: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  rejected: 'bg-red-50 text-red-700 border-red-300',
  revoked: 'bg-red-50 text-red-700 border-red-300',
  expired: 'bg-red-50 text-red-700 border-red-300',
};

function fmt(value) {
  return value || '-';
}

const workspaceVerificationMessage = (
  <>
    Verify your identity to activate this workspace and unlock the free tier.{' '}
    <Link to="/settings" className="font-medium underline">Go to Workspace verification</Link>
  </>
);

function fmtDate(value) {
  return value ? new Date(value).toLocaleString() : '-';
}

function fmtCompactDate(value) {
  const date = String(value || '').slice(0, 10);
  const match = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : '—';
}

function formatEnterpriseNumber(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length === 10 ? `${digits.slice(0, 4)}.${digits.slice(4, 7)}.${digits.slice(7)}` : value;
}

function StatusBadge({ status }) {
  const value = status || 'draft';
  const label = value === 'required' ? 'KYC REQUIRED' : value.replaceAll('_', ' ');
  return (
    <span className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 uppercase tracking-widest text-[10px] font-mono border ${badgeStyles[value] || badgeStyles.draft}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"/>
      {label}
    </span>
  );
}

function KycStatusBadge({ status }) {
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 uppercase tracking-widest text-[10px] font-mono border bg-zinc-50 text-zinc-600 border-zinc-200">
        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"/>
        Not linked
      </span>
    );
  }
  return <StatusBadge status={status}/>;
}

function SourceBadge({ source }) {
  const labels = {
    manual: 'MANUAL',
    kbo: 'KBO',
    registry: 'REGISTRY',
    ubo: 'UBO',
    api: 'API',
  };
  const value = labels[String(source || 'manual').toLowerCase()] || 'MANUAL';
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 uppercase tracking-widest text-[10px] font-mono border border-zinc-200 bg-white text-zinc-500">
      {value}
    </span>
  );
}

function formatRegistryStatus(value, source) {
  const status = value || (String(source || '').toLowerCase() === 'kbo' ? 'active' : null);
  if (status === 'active') return 'Active';
  if (status === 'inactive') return 'Inactive';
  return 'Unknown';
}

function RegistryStatusBadge({ status, source }) {
  const value = formatRegistryStatus(status, source);
  const tone = value === 'Active'
    ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
    : value === 'Inactive'
      ? 'border-red-300 bg-red-50 text-red-700'
      : 'border-zinc-200 bg-zinc-50 text-zinc-600';
  return (
    <span className={`inline-flex w-fit items-center px-1.5 py-0.5 uppercase tracking-widest text-[10px] font-mono border ${tone}`}>
      {value}
    </span>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div className="grid gap-1 border-b border-zinc-100 py-3 sm:grid-cols-[11rem_minmax(0,1fr)]">
      <div className="text-xs uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`${mono ? 'font-mono text-xs' : 'text-sm'} min-w-0 break-words text-zinc-900`}>{fmt(value)}</div>
    </div>
  );
}

function FieldContent({ label, children }) {
  return (
    <div className="grid gap-1 border-b border-zinc-100 py-3 sm:grid-cols-[11rem_minmax(0,1fr)]">
      <div className="text-xs uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="min-w-0 break-words text-sm text-zinc-900">{children}</div>
    </div>
  );
}

const checklistIndicatorStyles = {
  success: 'bg-green-50 text-green-700 border-green-300',
  warning: 'bg-amber-50 text-amber-700 border-amber-300',
  error: 'bg-red-50 text-red-700 border-red-300',
  pending: 'bg-zinc-50 text-zinc-500 border-zinc-300',
  neutral: 'bg-zinc-50 text-zinc-500 border-zinc-300',
};

function ChecklistIndicator({ tone, shape = 'round' }) {
  const resolvedTone = shape === 'round' && tone === 'pending' ? 'neutral' : tone || 'neutral';
  const shapeClass = shape === 'square' ? 'rounded-none h-7 w-7' : 'rounded-full h-6 w-6';
  return (
    <span className={`inline-flex shrink-0 items-center justify-center border ${shapeClass} ${checklistIndicatorStyles[resolvedTone] || checklistIndicatorStyles.neutral}`}>
      {resolvedTone === 'success' && <Check className="w-3 h-3" strokeWidth={2}/>}
      {resolvedTone === 'error' && <X className="w-3 h-3" strokeWidth={2}/>}
      {resolvedTone === 'pending' && <span className="h-3 w-3 rounded-full border border-current border-t-transparent opacity-70 animate-spin"/>}
      {(resolvedTone === 'neutral' || resolvedTone === 'warning') && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70"/>}
    </span>
  );
}

function ChecklistTimelineRow({ label, description, tone, shape = 'round' }) {
  const hasDescription = Boolean(description);
  return (
    <div className={`relative z-10 flex gap-4 py-2.5 ${hasDescription ? 'items-start' : 'items-center'}`}>
      <div className={`w-7 shrink-0 flex justify-center ${hasDescription ? 'pt-0.5' : ''}`}>
        <ChecklistIndicator tone={tone} shape={shape}/>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-zinc-500">{label}</span>
        </div>
        {description && <div className="mt-1 text-xs text-zinc-500 leading-relaxed">{description}</div>}
      </div>
    </div>
  );
}

function KybChecklistBox({ items }) {
  return (
    <div className="border border-zinc-200 rounded-sm bg-white">
      <div className="px-4 py-3">
        <span>
          <span className="block font-display text-lg font-semibold">Checklist</span>
          <span className="block text-sm text-zinc-500 mt-0.5">Current KYB workflow status.</span>
        </span>
      </div>
      <div className="px-4 pb-3 border-t border-zinc-200">
        <div className="relative mt-3">
          <div aria-hidden="true" className="absolute left-[13px] top-6 bottom-6 w-px bg-zinc-200/70"/>
          {items.map((item) => (
            <ChecklistTimelineRow
              key={item.label}
              label={item.label}
              description={item.description}
              tone={item.tone}
              shape={item.shape}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function getRepresentativeKycId(representative) {
  return (
    representative?.verification_session_id ||
    representative?.linked_kyc?.id ||
    representative?.linkedKyc?.id ||
    representative?.verification_session?.id ||
    representative?.verificationSession?.id ||
    representative?.linked_verification?.id ||
    representative?.linkedVerification?.id ||
    null
  );
}

function getRepresentativeKycStatus(representative) {
  return (
    representative?.verification_status ||
    representative?.linked_kyc?.status ||
    representative?.linkedKyc?.status ||
    representative?.verification_session?.status ||
    representative?.verificationSession?.status ||
    representative?.linked_verification?.status ||
    representative?.linkedVerification?.status ||
    null
  );
}

function getRepresentativeVerificationStatus(representative) {
  const kycId = getRepresentativeKycId(representative);
  if (!kycId) return representative?.required_for_verification ? 'required' : null;

  const status = String(getRepresentativeKycStatus(representative) || '').toLowerCase();
  if (!status) return 'not_linked';
  if (['approved', 'completed'].includes(status)) return 'approved';
  if (['pending', 'opened', 'in_progress', 'processing'].includes(status)) return 'pending';
  if (['rejected', 'revoked', 'expired', 'failed'].includes(status)) return 'rejected';
  return status;
}

function getUboVerificationStatus(ubo) {
  if (!ubo?.linked_verification_id) return ubo?.required_for_verification ? 'required' : null;
  const status = String(ubo?.verification_status || '').toLowerCase();
  if (!status) return 'not_linked';
  if (['approved', 'completed'].includes(status)) return 'approved';
  if (['pending', 'opened', 'in_progress', 'processing'].includes(status)) return 'pending';
  if (['rejected', 'revoked', 'expired', 'failed'].includes(status)) return 'rejected';
  return status;
}

function getRepresentativePersonName(representative) {
  return [representative?.first_name, representative?.last_name].filter(Boolean).join(' ')
    || representative?.display_name
    || null;
}

function isCompanyRepresentative(representative) {
  return Boolean(
    representative?.representative_enterprise_number
    && !representative?.first_name
    && !representative?.last_name
  );
}

function isTerminalStatus(status) {
  return terminalStatuses.has(String(status || '').toLowerCase());
}

function normalizeChecklistTone({ complete = false, active = false, terminal = false, failed = false, warning = false } = {}) {
  if (complete) return 'success';
  if (failed || (terminal && !complete)) return 'error';
  if (warning) return 'warning';
  if (active) return 'pending';
  return 'neutral';
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function formatDocumentType(value) {
  if (!value) return '';
  const labels = {
    id_card: 'ID card',
    passport: 'Passport',
    residence_permit: 'Residence permit',
  };
  const normalized = String(value).trim().toLowerCase();
  return labels[normalized] || normalized
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

const emptyForm = {
  business_name: '',
  enterprise_number: '',
  jurisdiction: 'BE',
  company_type: '',
  registered_address: '',
  website: '',
  contact_email: '',
  phone: '',
};

const emptyRepresentative = {
  first_name: '',
  last_name: '',
  title: '',
  email: '',
  source: 'manual',
  source_started_at: '',
  required_for_verification: false,
};

const emptyUbo = {
  full_name: '',
  date_of_birth: '',
  nationality: '',
  ownership_percentage: '',
  notes: '',
  required_for_verification: false,
};

export default function BusinessVerificationDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [record, setRecord] = useState(null);
  const [events, setEvents] = useState([]);
  const [representatives, setRepresentatives] = useState([]);
  const [ubos, setUbos] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [representativeForm, setRepresentativeForm] = useState(emptyRepresentative);
  const [representativeModalOpen, setRepresentativeModalOpen] = useState(false);
  const [uboForm, setUboForm] = useState(emptyUbo);
  const [uboModalOpen, setUboModalOpen] = useState(false);
  const [editingUboId, setEditingUboId] = useState(null);
  const [uboAttempted, setUboAttempted] = useState(false);
  const [representativeAttempted, setRepresentativeAttempted] = useState(false);
  const [editingRepresentativeId, setEditingRepresentativeId] = useState(null);
  const [linkKycRepresentative, setLinkKycRepresentative] = useState(null);
  const [linkKycUbo, setLinkKycUbo] = useState(null);
  const [eligibleKycSearch, setEligibleKycSearch] = useState('');
  const [eligibleKycItems, setEligibleKycItems] = useState([]);
  const [eligibleKycLoading, setEligibleKycLoading] = useState(false);
  const [linkingKycId, setLinkingKycId] = useState(null);
  const [pendingKycLink, setPendingKycLink] = useState(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importingKboRepresentatives, setImportingKboRepresentatives] = useState(false);
  const [updatingRequiredRepresentativeId, setUpdatingRequiredRepresentativeId] = useState(null);
  const [kboImportMessage, setKboImportMessage] = useState(null);
  const [creatingKycId, setCreatingKycId] = useState(null);
  const [message, setMessage] = useState(null);
  const [registrySnapshotOpen, setRegistrySnapshotOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [verifyConfirmationOpen, setVerifyConfirmationOpen] = useState(false);
  const [auditExpanded, setAuditExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get(`/business-verifications/${id}`);
    const next = data.business_verification;
    if (!next) {
      setRecord(null);
      setEvents([]);
      setRepresentatives([]);
      setUbos([]);
      setForm(emptyForm);
      setReason('');
      setLoading(false);
      return;
    }
    setRecord(next);
    setEvents(data.events || []);
    setForm({
      business_name: next.business_name || '',
      enterprise_number: next.enterprise_number || '',
      jurisdiction: next.jurisdiction || 'BE',
      company_type: next.company_type || '',
      registered_address: next.registered_address || '',
      website: next.website || '',
      contact_email: next.contact_email || '',
      phone: next.phone || '',
    });
    setReason(next.reason || '');
    const [reps, uboResponse] = await Promise.all([
      api.get(`/business-verifications/${id}/representatives`),
      api.get(`/business-verifications/${id}/ubos`),
    ]);
    setRepresentatives(reps.data.items || []);
    setUbos(uboResponse.data.items || []);
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const recordTerminal = isTerminalStatus(record?.status);
  const canEdit = record && !recordTerminal;
  const registrySnapshot = record?.registry_payload || {};
  const hasRegistrySnapshot = String(record?.source || '').toLowerCase() === 'kbo' && Object.keys(registrySnapshot).length > 0;
  const hasRegistryEvidence = Boolean(record?.registry_source || hasRegistrySnapshot);
  const representativeItems = Array.isArray(representatives) ? representatives.filter(Boolean) : [];
  const fixedRepresentativesByEnterprise = representativeItems.reduce((relationships, representative) => {
    const fixedRepresentative = representative?.representative_type === 'legal_representative'
      || String(representative?.role_label || representative?.title || '').toLowerCase() === 'vaste vertegenwoordiger';
    if (
      !fixedRepresentative
      || !representative?.representative_enterprise_number
      || isCompanyRepresentative(representative)
    ) return relationships;

    const personName = getRepresentativePersonName(representative);
    if (!personName) return relationships;
    const enterpriseNumber = representative.representative_enterprise_number;
    relationships[enterpriseNumber] = [...new Set([...(relationships[enterpriseNumber] || []), personName])];
    return relationships;
  }, {});
  const totalRepresentatives = representativeItems.length;
  const hasRepresentative = totalRepresentatives > 0;
  const requiredRepresentatives = representativeItems.filter((representative) => Boolean(representative?.required_for_verification));
  const requiredRepresentativeCount = requiredRepresentatives.length;
  const linkedRequiredRepresentatives = requiredRepresentatives.filter((representative) => Boolean(getRepresentativeKycId(representative)));
  const linkedRequiredRepresentativeCount = linkedRequiredRepresentatives.length;
  const requiredKycStatuses = linkedRequiredRepresentatives.map((representative) => String(
    getRepresentativeKycStatus(representative) || 'pending'
  ).toLowerCase());
  const approvedRequiredKycCount = requiredKycStatuses.filter((status) => ['approved', 'completed'].includes(status)).length;
  const allRequiredKycApproved = requiredRepresentativeCount > 0 && approvedRequiredKycCount === requiredRepresentativeCount;
  const requiredKycNeedsReview = requiredKycStatuses.some((status) => ['needs_review', 'needs_action', 'action_required'].includes(status));
  const requiredKycProblem = requiredKycStatuses.some((status) => ['rejected', 'revoked', 'expired', 'failed'].includes(status));
  const requiredKycIncomplete = requiredRepresentativeCount > 0 && !allRequiredKycApproved;
  const uboItems = Array.isArray(ubos) ? ubos.filter(Boolean) : [];
  const requiredUbos = uboItems.filter((ubo) => Boolean(ubo?.required_for_verification));
  const requiredUboCount = requiredUbos.length;
  const linkedRequiredUbos = requiredUbos.filter((ubo) => Boolean(ubo?.linked_verification_id));
  const requiredUboStatuses = linkedRequiredUbos.map((ubo) => String(ubo?.verification_status || 'pending').toLowerCase());
  const approvedRequiredUboCount = requiredUboStatuses.filter((status) => ['approved', 'completed'].includes(status)).length;
  const allRequiredUboKycApproved = requiredUboCount === 0 || approvedRequiredUboCount === requiredUboCount;
  const requiredUboKycIncomplete = !allRequiredUboKycApproved;
  const requiredUboKycProblem = requiredUboStatuses.some((status) => ['rejected', 'revoked', 'expired', 'failed'].includes(status));
  const requiredUboKycNeedsReview = requiredUboStatuses.some((status) => ['needs_review', 'needs_action', 'action_required'].includes(status));
  const canReview = ['admin', 'reviewer'].includes(user?.role);
  const representativeCountLabel = totalRepresentatives === 1 ? '1 representative added.' : `${totalRepresentatives} representatives added.`;
  const representativeKycProgressText = requiredRepresentativeCount > 0
    ? `${approvedRequiredKycCount}/${requiredRepresentativeCount} required approved.`
    : '0 required representatives.';
  const requiredKycIncompleteMessage = requiredRepresentativeCount === 0 || allRequiredKycApproved
    ? null
    : linkedRequiredRepresentativeCount === 0
      ? 'No required representative KYC linked'
      : requiredKycNeedsReview
        ? 'Required representative KYC needs review'
        : requiredKycProblem
          ? 'Required representative KYC rejected or revoked'
          : `Required representative KYC incomplete: ${approvedRequiredKycCount}/${requiredRepresentativeCount} approved`;
  const incompleteVerificationChecks = [
    !hasRegistryEvidence ? 'No registry evidence' : null,
    requiredKycIncompleteMessage,
    requiredUboKycIncomplete ? `Required UBO KYC incomplete: ${approvedRequiredUboCount}/${requiredUboCount} approved` : null,
  ].filter(Boolean);
  const businessSummarySource = record?.registry_source || (String(record?.source || '').toLowerCase() === 'kbo' ? 'KBO' : 'Manual');
  const representativeKycSummary = requiredRepresentativeCount > 0
    ? `${approvedRequiredKycCount}/${requiredRepresentativeCount} required approved`
    : '0 required representatives';
  const uboKycSummary = requiredUboCount > 0
    ? `${approvedRequiredUboCount}/${requiredUboCount} required approved`
    : '0 required UBOs';
  const verificationWorkflowTone = requiredRepresentativeCount === 0
    ? 'neutral'
    : requiredKycProblem
      ? 'error'
      : requiredKycNeedsReview
        ? 'warning'
        : allRequiredKycApproved
          ? 'success'
          : 'pending';
  const verificationWorkflowDescription = requiredRepresentativeCount === 0
    ? 'No required representatives selected yet.'
    : allRequiredKycApproved
      ? 'KYB verification completed successfully.'
      : 'Waiting for required representative(s) to complete the KYB verification flow.';
  const representativeKycTone = allRequiredKycApproved
    ? 'success'
    : requiredKycProblem
      ? 'error'
      : 'neutral';
  const reviewDecisionDescription = String(record?.status || '').toLowerCase() === 'verified'
    ? 'Business verification approved.'
    : String(record?.status || '').toLowerCase() === 'needs_review'
      ? 'Business verification marked for review.'
      : String(record?.status || '').toLowerCase() === 'rejected'
        ? 'Business verification rejected.'
        : String(record?.status || '').toLowerCase() === 'revoked'
          ? 'Business verification revoked.'
          : 'No review decision recorded.';
  const reviewDecisionTone = String(record?.status || '').toLowerCase() === 'verified'
    ? 'success'
    : String(record?.status || '').toLowerCase() === 'needs_review'
      ? 'warning'
      : ['rejected', 'revoked'].includes(String(record?.status || '').toLowerCase())
        ? 'error'
        : 'neutral';
  const kybWorkflowChecklist = [
    {
      label: 'Setup',
      description: hasRepresentative
        ? 'Business setup completed successfully.'
        : 'Add at least one representative to this verification flow.',
      tone: normalizeChecklistTone({
        complete: hasRepresentative,
        active: !hasRepresentative && !recordTerminal,
        terminal: recordTerminal,
      }),
      shape: 'square',
    },
    {
      label: 'Registry evidence',
      description: hasRegistryEvidence ? 'Registry evidence captured.' : 'No registry evidence captured.',
      tone: hasRegistryEvidence ? 'success' : 'neutral',
      shape: 'round',
    },
    {
      label: 'Representative',
      description: hasRepresentative ? representativeCountLabel : 'No representatives added.',
      tone: hasRepresentative ? 'success' : 'neutral',
      shape: 'round',
    },
    {
      label: 'Verification',
      description: verificationWorkflowDescription,
      tone: verificationWorkflowTone,
      shape: 'square',
    },
    {
      label: 'Representative KYC',
      description: representativeKycProgressText,
      tone: representativeKycTone,
      shape: 'round',
    },
    {
      label: 'UBOs reviewed',
      description: uboItems.length > 0 ? `${uboItems.length} UBO${uboItems.length === 1 ? '' : 's'} added.` : 'No UBOs added.',
      tone: uboItems.length > 0 ? 'success' : 'neutral',
      shape: 'round',
    },
    {
      label: 'Required UBO KYC',
      description: requiredUboCount > 0 ? `${approvedRequiredUboCount}/${requiredUboCount} required approved.` : 'No required UBO KYC.',
      tone: requiredUboKycProblem ? 'error' : requiredUboKycNeedsReview ? 'warning' : allRequiredUboKycApproved && requiredUboCount > 0 ? 'success' : 'neutral',
      shape: 'round',
    },
    {
      label: 'Review decision',
      description: reviewDecisionDescription,
      tone: reviewDecisionTone,
      shape: 'round',
    },
  ];
  const eventItems = useMemo(() => (Array.isArray(events) ? events : []).map((event) => ({
    ...(event || {}),
    summary: event?.payload?.business_name
      || event?.payload?.full_name
      || [event?.payload?.first_name, event?.payload?.last_name].filter(Boolean).join(' ')
      || event?.payload?.reason
      || event?.payload?.status
      || '',
  })), [events]);
  const auditTrailItems = useMemo(() => [...eventItems].reverse(), [eventItems]);
  const trimmedRepresentativeForm = {
    first_name: representativeForm.first_name.trim(),
    last_name: representativeForm.last_name.trim(),
    title: representativeForm.title.trim(),
    email: representativeForm.email.trim(),
    source: representativeForm.source,
    source_started_at: representativeForm.source_started_at || null,
    required_for_verification: Boolean(representativeForm.required_for_verification),
  };
  const manualRepresentativeForm = trimmedRepresentativeForm.source === 'manual';
  const representativeErrors = {
    first_name: !manualRepresentativeForm || trimmedRepresentativeForm.first_name ? '' : 'First name is required.',
    last_name: !manualRepresentativeForm || trimmedRepresentativeForm.last_name ? '' : 'Last name is required.',
    title: !manualRepresentativeForm || trimmedRepresentativeForm.title ? '' : 'Role / title is required.',
    email: manualRepresentativeForm && !trimmedRepresentativeForm.email
      ? 'Email is required.'
      : !trimmedRepresentativeForm.email || isValidEmail(trimmedRepresentativeForm.email)
        ? ''
        : 'Enter a valid email address.',
  };
  const representativeFormValid = Object.values(representativeErrors).every((error) => !error);
  const trimmedUboForm = {
    full_name: uboForm.full_name.trim(),
    date_of_birth: uboForm.date_of_birth || null,
    nationality: uboForm.nationality.trim() || null,
    ownership_percentage: uboForm.ownership_percentage === '' ? null : Number(uboForm.ownership_percentage),
    notes: uboForm.notes.trim() || null,
    required_for_verification: Boolean(uboForm.required_for_verification),
  };
  const uboErrors = {
    full_name: trimmedUboForm.full_name ? '' : 'Full name is required.',
    ownership_percentage: trimmedUboForm.ownership_percentage === null
      || (Number.isFinite(trimmedUboForm.ownership_percentage)
        && trimmedUboForm.ownership_percentage >= 0
        && trimmedUboForm.ownership_percentage <= 100)
      ? ''
      : 'Enter a percentage between 0 and 100.',
  };
  const uboFormValid = Object.values(uboErrors).every((error) => !error);

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const setRepresentativeField = (field, value) => {
    setRepresentativeForm((current) => ({ ...current, [field]: value }));
  };

  const setUboField = (field, value) => {
    setUboForm((current) => ({ ...current, [field]: value }));
  };

  const loadEligibleKyc = useCallback(async (targetType, targetId, q = '') => {
    if (!targetId) return;
    setEligibleKycLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get(`/business-verifications/${id}/${targetType}/${targetId}/eligible-kyc`, { params });
      setEligibleKycItems(data.items || []);
    } catch {
      setEligibleKycItems([]);
    } finally {
      setEligibleKycLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const target = linkKycRepresentative || linkKycUbo;
    if (!target?.id) return undefined;
    const timer = setTimeout(() => {
      loadEligibleKyc(linkKycRepresentative ? 'representatives' : 'ubos', target.id, eligibleKycSearch);
    }, 250);
    return () => clearTimeout(timer);
  }, [eligibleKycSearch, linkKycRepresentative, linkKycUbo, loadEligibleKyc]);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await api.patch(`/business-verifications/${id}`, form);
      setRecord(data.business_verification);
      setMessage('Business details saved.');
      await load();
    } catch {
      setMessage('Could not save business details.');
    } finally {
      setSaving(false);
    }
  };

  const decide = async (status) => {
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await api.post(`/business-verifications/${id}/decision`, { status, reason: reason || null });
      setRecord(data.business_verification);
      setMessage('Decision saved.');
      await load();
    } catch (error) {
      setMessage(error?.response?.data?.error === 'required_representative_kyc_incomplete'
        ? 'All required representatives and UBOs must have approved KYC before verification.'
        : 'Could not save decision.');
    } finally {
      setSaving(false);
    }
  };

  const markVerified = () => {
    if (requiredKycIncomplete || requiredUboKycIncomplete) {
      setMessage('All required representatives and UBOs must have approved KYC before verification.');
      return;
    }
    if (incompleteVerificationChecks.length > 0) {
      setVerifyConfirmationOpen(true);
      return;
    }
    decide('verified');
  };

  const confirmMarkVerified = async () => {
    setVerifyConfirmationOpen(false);
    await decide('verified');
  };

  const revoke = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await api.post(`/business-verifications/${id}/revoke`, { reason: reason || null });
      if (data.business_verification) setRecord(data.business_verification);
      setMessage('Business verification revoked.');
      await load();
    } catch {
      setMessage('Could not revoke business verification.');
    } finally {
      setSaving(false);
    }
  };

  const resetRepresentativeForm = () => {
    setRepresentativeForm(emptyRepresentative);
    setEditingRepresentativeId(null);
    setRepresentativeAttempted(false);
    setRepresentativeModalOpen(false);
  };

  const openAddRepresentativeModal = () => {
    setRepresentativeForm(emptyRepresentative);
    setEditingRepresentativeId(null);
    setRepresentativeAttempted(false);
    setRepresentativeModalOpen(true);
  };

  const importKboRepresentatives = async () => {
    setImportingKboRepresentatives(true);
    setKboImportMessage(null);
    try {
      const { data } = await api.post(`/business-verifications/${id}/import-kbo-representatives`);
      setKboImportMessage(`${data.imported_count || 0} imported, ${data.skipped_count || 0} skipped.`);
      await load();
    } catch (error) {
      const code = error?.response?.data?.error;
      setKboImportMessage(code === 'missing_enterprise_number'
        ? 'Add an enterprise number before importing from KBO.'
        : 'Could not import KBO representatives.');
    } finally {
      setImportingKboRepresentatives(false);
    }
  };

  const setRepresentativeRequired = async (representative, required) => {
    if (!representative?.id || !canReview) return;
    setUpdatingRequiredRepresentativeId(representative.id);
    setMessage(null);
    try {
      await api.post(`/business-verifications/${id}/representatives/${representative.id}/required`, {
        required_for_verification: required,
      });
      setMessage(required ? 'Representative marked required.' : 'Representative marked optional.');
      await load();
    } catch {
      setMessage('Could not update representative requirement.');
    } finally {
      setUpdatingRequiredRepresentativeId(null);
    }
  };

  const saveRepresentative = async () => {
    setRepresentativeAttempted(true);
    if (!representativeFormValid) return;
    setSaving(true);
    setMessage(null);
    try {
      if (editingRepresentativeId) {
        await api.patch(`/business-verifications/${id}/representatives/${editingRepresentativeId}`, trimmedRepresentativeForm);
        const currentRepresentative = representativeItems.find((representative) => representative?.id === editingRepresentativeId);
        if (Boolean(currentRepresentative?.required_for_verification) !== trimmedRepresentativeForm.required_for_verification) {
          await api.post(`/business-verifications/${id}/representatives/${editingRepresentativeId}/required`, {
            required_for_verification: trimmedRepresentativeForm.required_for_verification,
          });
        }
        setMessage('Representative updated.');
      } else {
        const { data } = await api.post(`/business-verifications/${id}/representatives`, trimmedRepresentativeForm);
        if (trimmedRepresentativeForm.required_for_verification && data.representative?.id) {
          await api.post(`/business-verifications/${id}/representatives/${data.representative.id}/required`, {
            required_for_verification: true,
          });
        }
        setMessage('Representative added.');
      }
      resetRepresentativeForm();
      await load();
    } catch {
      setMessage('Could not save representative.');
    } finally {
      setSaving(false);
    }
  };

  const editRepresentative = (representative) => {
    setEditingRepresentativeId(representative.id);
    setRepresentativeForm({
      first_name: representative.first_name || '',
      last_name: representative.last_name || '',
      title: representative.title || '',
      email: representative.email || '',
      source: representative.source || 'manual',
      source_started_at: String(representative.source_started_at || '').slice(0, 10),
      required_for_verification: Boolean(representative.required_for_verification),
    });
    setRepresentativeAttempted(false);
    setRepresentativeModalOpen(true);
  };

  const openAddUboModal = () => {
    setUboForm(emptyUbo);
    setEditingUboId(null);
    setUboAttempted(false);
    setUboModalOpen(true);
  };

  const editUbo = (ubo) => {
    setEditingUboId(ubo.id);
    setUboForm({
      full_name: ubo.full_name || '',
      date_of_birth: String(ubo.date_of_birth || '').slice(0, 10),
      nationality: ubo.nationality || '',
      ownership_percentage: ubo.ownership_percentage ?? '',
      notes: ubo.notes || '',
      required_for_verification: Boolean(ubo.required_for_verification),
    });
    setUboAttempted(false);
    setUboModalOpen(true);
  };

  const closeUboModal = () => {
    setUboModalOpen(false);
    setEditingUboId(null);
    setUboAttempted(false);
    setUboForm(emptyUbo);
  };

  const saveUbo = async () => {
    setUboAttempted(true);
    if (!uboFormValid) return;
    setSaving(true);
    setMessage(null);
    try {
      if (editingUboId) {
        await api.put(`/business-verifications/${id}/ubos/${editingUboId}`, trimmedUboForm);
        setMessage('UBO updated.');
      } else {
        await api.post(`/business-verifications/${id}/ubos`, trimmedUboForm);
        setMessage('UBO added.');
      }
      closeUboModal();
      await load();
    } catch {
      setMessage('Could not save UBO.');
    } finally {
      setSaving(false);
    }
  };

  const setUboRequired = async (ubo, required) => {
    setSaving(true);
    setMessage(null);
    try {
      await api.post(`/business-verifications/${id}/ubos/${ubo.id}/${required ? 'mark-required' : 'mark-optional'}`);
      setMessage(required ? 'UBO marked required.' : 'UBO marked optional.');
      await load();
    } catch {
      setMessage('Could not update UBO requirement.');
    } finally {
      setSaving(false);
    }
  };

  const createUboKyc = async (ubo) => {
    setSaving(true);
    setMessage(null);
    try {
      await api.post(`/business-verifications/${id}/ubos/${ubo.id}/create-kyc`);
      setMessage('UBO KYC verification created and linked.');
      await load();
    } catch (error) {
      const code = error?.response?.data?.code || error?.response?.data?.error;
      setMessage(code === 'workspace_verification_required'
        ? workspaceVerificationMessage
        : 'Could not create UBO KYC verification.');
    } finally {
      setSaving(false);
    }
  };

  const openLinkExistingUboKyc = (ubo) => {
    setLinkKycUbo(ubo);
    setLinkKycRepresentative(null);
    setEligibleKycSearch('');
    setEligibleKycItems([]);
    setPendingKycLink(null);
  };

  const unlinkUboVerification = async (ubo) => {
    setSaving(true);
    setMessage(null);
    try {
      await api.post(`/business-verifications/${id}/ubos/${ubo.id}/unlink-verification`);
      setMessage('UBO KYC verification unlinked.');
      await load();
    } catch {
      setMessage('Could not unlink UBO KYC verification.');
    } finally {
      setSaving(false);
    }
  };

  const deleteUbo = async (ubo) => {
    setSaving(true);
    setMessage(null);
    try {
      await api.delete(`/business-verifications/${id}/ubos/${ubo.id}`);
      setMessage('UBO removed.');
      await load();
    } catch {
      setMessage('Could not remove UBO.');
    } finally {
      setSaving(false);
    }
  };

  const deleteRepresentative = async (representativeId) => {
    setSaving(true);
    setMessage(null);
    try {
      await api.delete(`/business-verifications/${id}/representatives/${representativeId}`);
      setMessage('Representative removed.');
      await load();
    } catch {
      setMessage('Could not delete representative.');
    } finally {
      setSaving(false);
    }
  };

  const confirmRepresentativeAction = async () => {
    if (!confirmAction) return;
    const action = confirmAction;
    setConfirmAction(null);
    if (action.type === 'unlink') {
      await unlinkRepresentativeVerification(action.representativeId);
      return;
    }
    if (action.type === 'remove') {
      await deleteRepresentative(action.representativeId);
    }
  };

  const openLinkExistingKyc = (representative) => {
    setLinkKycRepresentative(representative);
    setLinkKycUbo(null);
    setEligibleKycSearch('');
    setEligibleKycItems([]);
    setPendingKycLink(null);
  };

  const closeLinkExistingKyc = () => {
    setLinkKycRepresentative(null);
    setLinkKycUbo(null);
    setEligibleKycSearch('');
    setEligibleKycItems([]);
    setPendingKycLink(null);
    setLinkingKycId(null);
  };

  const linkExistingKyc = async (verification, { confirmed = false } = {}) => {
    const target = linkKycRepresentative || linkKycUbo;
    if (!target?.id || !verification?.id) return;
    if (!confirmed && String(verification.status || '').toLowerCase() !== 'approved') {
      setPendingKycLink(verification);
      return;
    }
    setLinkingKycId(verification.id);
    setMessage(null);
    try {
      const endpoint = linkKycRepresentative
        ? `/business-verifications/${id}/representatives/${target.id}/link-kyc`
        : `/business-verifications/${id}/ubos/${target.id}/link-verification`;
      await api.post(endpoint, {
        verification_session_id: verification.id,
      });
      setMessage('KYC verification linked.');
      closeLinkExistingKyc();
      await load();
    } catch {
      setMessage('Could not link KYC verification.');
    } finally {
      setLinkingKycId(null);
    }
  };

  const unlinkRepresentativeVerification = async (representativeId) => {
    setSaving(true);
    setMessage(null);
    try {
      await api.post(`/business-verifications/${id}/representatives/${representativeId}/unlink-verification`);
      setMessage('KYC verification unlinked.');
      await load();
    } catch {
      setMessage('Could not unlink KYC verification.');
    } finally {
      setSaving(false);
    }
  };

  const createRepresentativeKyc = async (representativeId) => {
    setCreatingKycId(representativeId);
    setMessage(null);
    try {
      await api.post(`/business-verifications/${id}/representatives/${representativeId}/create-kyc`);
      setMessage('KYC verification created and linked.');
      await load();
    } catch (error) {
      const code = error?.response?.data?.code || error?.response?.data?.error;
      setMessage(code === 'workspace_verification_required'
        ? workspaceVerificationMessage
        : code === 'usage_limit_exhausted'
          ? 'No KYC verification usage available. Revoke an unused link or wait until next period.'
          : 'Could not create KYC verification.');
    } finally {
      setCreatingKycId(null);
    }
  };

  const decideRepresentative = async (representativeId, status) => {
    setSaving(true);
    setMessage(null);
    try {
      await api.post(`/business-verifications/${id}/representatives/${representativeId}/decision`, { status });
      setMessage('Representative decision saved.');
      await load();
    } catch {
      setMessage('Could not save representative decision.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-zinc-500">Loading...</div>;
  if (!record) return <div className="text-sm text-zinc-500">Business verification not found.</div>;

  return (
    <div data-testid="business-verification-detail-page">
      <VerificationBreadcrumb rootLabel="Businesses" rootTo="/dashboard/businesses" current={record.business_name}/>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div className="min-w-0">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Business verification</p>
          <h1 className="mt-1 flex min-w-0 items-start gap-2 font-display text-3xl font-bold tracking-tight">
            <Building2 className="mt-1 h-6 w-6 shrink-0 text-zinc-400" strokeWidth={1.5}/>
            <span className="min-w-0 break-words">{record.business_name}</span>
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <StatusBadge status={record?.effective_status || record?.status}/>
            <span className="text-xs font-mono text-zinc-500">id: {record.id}</span>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)] lg:items-start">
        <section className="border border-zinc-200 rounded-sm bg-white p-6">
          <h2 className="font-display text-lg font-semibold">Business summary</h2>
          <div className="mt-3">
            <Field label="Business" value={record.business_name}/>
            <Field label="Enterprise number" value={record.enterprise_number} mono/>
            <Field label="Jurisdiction" value={record.jurisdiction} mono/>
            <Field label="Source" value={businessSummarySource}/>
            <Field label="Representatives" value={String(totalRepresentatives)}/>
            <Field label="Representative KYC" value={representativeKycSummary} mono/>
            <Field label="UBOs" value={String(uboItems.length)}/>
            <Field label="Required UBO KYC" value={uboKycSummary} mono/>
          </div>
          <div className="mt-5">
            <KybChecklistBox items={kybWorkflowChecklist}/>
          </div>
        </section>

        <div className="min-w-0 space-y-4">
          <AuditTrail
            events={auditTrailItems}
            className="w-full flex"
            previewHeight={360}
            expanded={auditExpanded}
            onExpand={() => setAuditExpanded(true)}
            onCollapse={() => setAuditExpanded(false)}
          />

          <section className="border border-zinc-200 rounded-sm bg-white p-6">
            <h2 className="font-display text-lg font-semibold">Manual review</h2>
            <p className="text-sm text-zinc-500 mt-1">Record a reviewer decision for this business verification.</p>
            <label className="block mt-5">
              <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Reason</span>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} disabled={record?.status === 'revoked'}
                className="w-full min-h-24 border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black disabled:bg-zinc-50"/>
            </label>
            <div className="mt-4 grid gap-2">
              <button type="button" onClick={markVerified} disabled={saving || record?.status === 'revoked'}
                className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50">
                Mark verified
              </button>
              <button type="button" onClick={() => decide('needs_review')} disabled={saving || record?.status === 'revoked'}
                className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400 disabled:opacity-50">
                Needs review
              </button>
              <button type="button" onClick={() => decide('rejected')} disabled={saving || record?.status === 'revoked'}
                className="text-sm px-3 py-2 border border-red-200 text-red-700 rounded-sm hover:border-red-300 disabled:opacity-50">
                Reject
              </button>
              <button type="button" onClick={revoke} disabled={saving || record?.status === 'revoked'}
                className="text-sm px-3 py-2 border border-zinc-200 text-zinc-700 rounded-sm hover:border-zinc-400 disabled:opacity-50">
                Revoke
              </button>
            </div>
            {message && <p className="mt-3 text-sm text-zinc-600">{message}</p>}
          </section>
        </div>
        </div>

        <section className="border border-zinc-200 rounded-sm bg-white p-6">
          <h2 className="font-display text-lg font-semibold">Company details</h2>
          <p className="text-sm text-zinc-500 mt-1">Manual business evidence for this KYB record.</p>

          {canEdit ? (
            <div className="mt-5 space-y-4">
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Business name</span>
                <input value={form.business_name} onChange={(e) => setField('business_name', e.target.value)}
                  className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Enterprise number</span>
                  <input value={form.enterprise_number} onChange={(e) => setField('enterprise_number', e.target.value)}
                    className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
                </label>
                <label className="block">
                  <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Jurisdiction</span>
                  <input value={form.jurisdiction} onChange={(e) => setField('jurisdiction', e.target.value)}
                    className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
                </label>
              </div>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Company type</span>
                <input value={form.company_type} onChange={(e) => setField('company_type', e.target.value)}
                  className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
              </label>
              <FieldContent label="Registry status"><RegistryStatusBadge status={record.registry_status} source={record.source}/></FieldContent>
              <Field label="Registry source" value={record.registry_source === 'kbo' ? 'KBO' : record.registry_source}/>
              <Field label="Dataset date" value={fmtDate(registrySnapshot.registry_dataset_date || registrySnapshot.dataset_metadata?.registry_dataset_date)} mono/>
              <Field label="Fetched at" value={fmtDate(record.registry_fetched_at)} mono/>
              <Field label="Manual changes" value={record.has_manual_overrides ? 'Yes' : 'No'}/>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Registered address</span>
                <textarea value={form.registered_address} onChange={(e) => setField('registered_address', e.target.value)}
                  className="w-full min-h-24 border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Website</span>
                  <input value={form.website} onChange={(e) => setField('website', e.target.value)}
                    className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
                </label>
                <label className="block">
                  <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Contact email</span>
                  <input value={form.contact_email} onChange={(e) => setField('contact_email', e.target.value)}
                    className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
                </label>
              </div>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Phone</span>
                <input value={form.phone} onChange={(e) => setField('phone', e.target.value)}
                  className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
              </label>
              <button type="button" onClick={save} disabled={saving || !form.business_name.trim()}
                className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save details'}
              </button>
            </div>
          ) : (
            <div className="mt-5">
              <Field label="Business name" value={record.business_name}/>
              <Field label="Enterprise number" value={record.enterprise_number} mono/>
              <Field label="Jurisdiction" value={record.jurisdiction} mono/>
              <Field label="Company type" value={record.company_type}/>
              <FieldContent label="Registry status"><RegistryStatusBadge status={record.registry_status} source={record.source}/></FieldContent>
              <Field label="Registry source" value={record.registry_source === 'kbo' ? 'KBO' : record.registry_source}/>
              <Field label="Dataset date" value={fmtDate(registrySnapshot.registry_dataset_date || registrySnapshot.dataset_metadata?.registry_dataset_date)} mono/>
              <Field label="Fetched at" value={fmtDate(record.registry_fetched_at)} mono/>
              <Field label="Manual changes" value={record.has_manual_overrides ? 'Yes' : 'No'}/>
              <Field label="Registered address" value={record.registered_address}/>
              <Field label="Website" value={record.website} mono/>
              <Field label="Contact email" value={record.contact_email} mono/>
              <Field label="Phone" value={record.phone} mono/>
            </div>
          )}
        </section>

        {hasRegistrySnapshot && (
          <section className="border border-zinc-200 rounded-sm bg-white p-6">
            <button type="button" onClick={() => setRegistrySnapshotOpen((open) => !open)}
              className="flex w-full items-start justify-between gap-4 text-left">
              <div>
                <h2 className="font-display text-lg font-semibold">Registry snapshot</h2>
                <p className="text-sm text-zinc-500 mt-1">Official registry values captured when this record was created.</p>
              </div>
              <span className="shrink-0 text-xs font-mono uppercase tracking-widest text-zinc-500">
                {registrySnapshotOpen ? 'Hide' : 'Show'}
              </span>
            </button>
            {registrySnapshotOpen && (
              <div className="mt-5">
                <Field label="Business name" value={registrySnapshot.business_name}/>
                <Field label="Enterprise number" value={registrySnapshot.formatted_enterprise_number || registrySnapshot.enterprise_number} mono/>
                <Field label="Company type" value={registrySnapshot.company_type}/>
                <Field label="Registered address" value={registrySnapshot.registered_address}/>
                <Field label="Website" value={registrySnapshot.website} mono/>
                <Field label="Contact email" value={registrySnapshot.contact_email} mono/>
                <Field label="Phone" value={registrySnapshot.phone} mono/>
                <FieldContent label="Registry status">
                  <RegistryStatusBadge status={registrySnapshot.registry_status} source={registrySnapshot.source}/>
                </FieldContent>
                <Field label="Source" value={registrySnapshot.source === 'kbo' ? 'KBO' : registrySnapshot.source}/>
                <Field label="Dataset date" value={fmtDate(registrySnapshot.registry_dataset_date || registrySnapshot.dataset_metadata?.registry_dataset_date)} mono/>
                <Field label="Fetched at" value={fmtDate(record.registry_fetched_at)} mono/>
              </div>
            )}
          </section>
        )}

        <section className="border border-zinc-200 rounded-sm bg-white p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-semibold">Representatives</h2>
              <p className="text-sm text-zinc-500 mt-1">Add directors, UBOs, legal representatives, or authorized signers and optionally link them to an individual verification.</p>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button type="button" onClick={importKboRepresentatives}
                disabled={importingKboRepresentatives || !record?.enterprise_number}
                className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400 disabled:opacity-50">
                {importingKboRepresentatives ? 'Importing...' : 'Import from KBO'}
              </button>
              <button type="button" onClick={openAddRepresentativeModal}
                className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50">
                Add representative
              </button>
            </div>
          </div>
          {kboImportMessage && <p className="mt-3 text-sm text-zinc-600">{kboImportMessage}</p>}

          <div className="mt-5 border border-zinc-200 rounded-sm overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-xs uppercase tracking-widest text-zinc-500">
                  <th className="text-left px-4 py-3 font-medium">Representative</th>
                  <th className="text-left px-4 py-3 font-medium">Source</th>
                  <th className="text-left px-4 py-3 font-medium">Since</th>
                  <th className="text-left px-4 py-3 font-medium">Verification</th>
                  <th className="px-4 py-3 font-medium text-right" aria-label="Representative actions"></th>
                </tr>
              </thead>
              <tbody>
                {representativeItems.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-8 text-sm text-zinc-500">No representatives yet.</td></tr>
                ) : representativeItems.map((representative, index) => {
                  const kycId = getRepresentativeKycId(representative);
                  const requiredForVerification = Boolean(representative?.required_for_verification);
                  const verificationStatus = getRepresentativeVerificationStatus(representative);
                  const companyRepresentative = isCompanyRepresentative(representative);
                  const name = companyRepresentative
                    ? representative?.representative_company_name || 'Company representative'
                    : getRepresentativePersonName(representative) || 'Unnamed representative';
                  const representedBy = companyRepresentative
                    ? fixedRepresentativesByEnterprise[representative.representative_enterprise_number] || []
                    : [];
                  const role = representative?.title || representative?.role_label || 'Representative';
                  const source = String(representative?.source || 'manual').toLowerCase();
                  return (
                    <tr key={representative?.id || `representative-${index}`} className="border-b border-zinc-100">
                      <td className="px-4 py-3 align-middle min-w-0">
                        <div className="flex items-start gap-2">
                          {companyRepresentative
                            ? <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" strokeWidth={1.5}/>
                            : <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" strokeWidth={1.5}/>}
                          <div className="min-w-0">
                            <div className="font-medium truncate" title={name}>{name}</div>
                            <div className="mt-0.5 text-xs text-zinc-500">
                              {companyRepresentative && representedBy.length > 0 ? (
                                <><span aria-hidden="true">↳ </span>Represented by {representedBy.join(', ')}</>
                              ) : (
                                role
                              )}
                            </div>
                            {companyRepresentative && !representative?.representative_company_name && (
                              <div className="mt-0.5 text-xs font-mono text-zinc-500">{formatEnterpriseNumber(representative.representative_enterprise_number)}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <SourceBadge source={source}/>
                      </td>
                      <td className="px-4 py-3 align-middle text-xs font-mono text-zinc-500">
                        {fmtCompactDate(representative?.source_started_at)}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        {verificationStatus ? <StatusBadge status={verificationStatus}/> : null}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button type="button"
                                disabled={saving || !representative?.id}
                                className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 border border-zinc-200 rounded-sm hover:border-zinc-400 disabled:opacity-50">
                                <MoreHorizontal className="h-3 w-3" strokeWidth={1.7}/>
                                More
                                <ChevronDown className="h-3 w-3" strokeWidth={1.7}/>
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end"
                              className="min-w-40 rounded-sm border-zinc-200 bg-white p-1 text-zinc-700 shadow-md">
                              {!requiredForVerification && (
                                <DropdownMenuItem
                                  disabled={!canReview || recordTerminal || updatingRequiredRepresentativeId === representative?.id}
                                  onSelect={() => setRepresentativeRequired(representative, true)}
                                  className="cursor-pointer rounded-sm px-2 py-1 text-xs text-zinc-800 hover:bg-zinc-100 hover:text-zinc-900 focus:bg-zinc-100 focus:text-zinc-900 data-[disabled]:cursor-not-allowed data-[disabled]:bg-zinc-50 data-[disabled]:text-zinc-400">
                                  <PlusCircle className="h-3 w-3 text-zinc-500" strokeWidth={1.7}/>
                                  Add verification
                                </DropdownMenuItem>
                              )}
                              {requiredForVerification && !kycId && (
                                <>
                                  <DropdownMenuItem
                                    disabled={creatingKycId === representative?.id}
                                    onSelect={() => createRepresentativeKyc(representative?.id)}
                                    className="cursor-pointer rounded-sm px-2 py-1 text-xs text-zinc-800 hover:bg-zinc-100 hover:text-zinc-900 focus:bg-zinc-100 focus:text-zinc-900 data-[disabled]:cursor-not-allowed data-[disabled]:bg-zinc-50 data-[disabled]:text-zinc-400">
                                    <Plus className="h-3 w-3 text-zinc-500" strokeWidth={1.7}/>
                                    {creatingKycId === representative?.id ? 'Creating...' : 'Create KYC session'}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onSelect={() => openLinkExistingKyc(representative)}
                                    className="cursor-pointer rounded-sm px-2 py-1 text-xs text-zinc-800 hover:bg-zinc-100 hover:text-zinc-900 focus:bg-zinc-100 focus:text-zinc-900 data-[disabled]:cursor-not-allowed data-[disabled]:bg-zinc-50 data-[disabled]:text-zinc-400">
                                    <LinkIcon className="h-3 w-3 text-zinc-500" strokeWidth={1.7}/>
                                    Link KYC session
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator className="bg-zinc-100"/>
                                </>
                              )}
                              {kycId && (
                                <DropdownMenuItem asChild className="cursor-pointer rounded-sm px-2 py-1 text-xs text-zinc-800 hover:bg-zinc-100 hover:text-zinc-900 focus:bg-zinc-100 focus:text-zinc-900 data-[disabled]:cursor-not-allowed data-[disabled]:bg-zinc-50 data-[disabled]:text-zinc-400">
                                  <Link to={`/dashboard/verifications/${kycId}`}>
                                    <Eye className="h-3 w-3 text-zinc-500" strokeWidth={1.7}/>
                                    View KYC session
                                  </Link>
                                </DropdownMenuItem>
                              )}
                              {requiredForVerification && (
                                <DropdownMenuItem
                                  disabled={!canReview || recordTerminal || updatingRequiredRepresentativeId === representative?.id}
                                  onSelect={() => setRepresentativeRequired(representative, false)}
                                  className="cursor-pointer rounded-sm px-2 py-1 text-xs text-zinc-800 hover:bg-zinc-100 hover:text-zinc-900 focus:bg-zinc-100 focus:text-zinc-900 data-[disabled]:cursor-not-allowed data-[disabled]:bg-zinc-50 data-[disabled]:text-zinc-400">
                                  <MinusCircle className="h-3 w-3 text-zinc-500" strokeWidth={1.7}/>
                                  Remove verification
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <button type="button" onClick={() => editRepresentative(representative)}
                            disabled={!representative?.id}
                            className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 border border-zinc-200 rounded-sm hover:border-zinc-400">
                            <Pencil className="h-3 w-3" strokeWidth={1.7}/>
                            Edit
                          </button>
                          <button type="button" onClick={() => setConfirmAction({ type: 'remove', representativeId: representative?.id })} disabled={saving || !representative?.id}
                            className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 border border-red-200 bg-white text-red-700 rounded-sm hover:border-red-300 disabled:opacity-50">
                            <Trash2 className="h-3 w-3" strokeWidth={1.7}/>
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className="border border-zinc-200 rounded-sm bg-white p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-semibold">Beneficial owners (UBOs)</h2>
              <p className="text-sm text-zinc-500 mt-1">Add and verify the ultimate beneficial owners relevant to this KYB review.</p>
            </div>
            <button type="button" onClick={openAddUboModal} disabled={!canReview || recordTerminal}
              className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50">
              Add UBO
            </button>
          </div>
          <div className="mt-5 border border-zinc-200 rounded-sm overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-xs uppercase tracking-widest text-zinc-500">
                  <th className="text-left px-4 py-3 font-medium">UBO</th>
                  <th className="text-left px-4 py-3 font-medium">Source</th>
                  <th className="text-left px-4 py-3 font-medium">Ownership</th>
                  <th className="text-left px-4 py-3 font-medium">Verification</th>
                  <th className="px-4 py-3 font-medium text-right" aria-label="UBO actions"></th>
                </tr>
              </thead>
              <tbody>
                {uboItems.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-8 text-sm text-zinc-500">No beneficial owners yet.</td></tr>
                ) : uboItems.map((ubo) => {
                  const linkedKycId = ubo.linked_verification_id;
                  const verificationStatus = getUboVerificationStatus(ubo);
                  const required = Boolean(ubo.required_for_verification);
                  const secondary = [ubo.date_of_birth ? `DOB ${fmtCompactDate(ubo.date_of_birth)}` : null, ubo.nationality].filter(Boolean).join(' · ');
                  return (
                    <tr key={ubo.id} className="border-b border-zinc-100">
                      <td className="px-4 py-3 align-middle">
                        <div className="flex items-start gap-2">
                          <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" strokeWidth={1.5}/>
                          <div>
                            <div className="font-medium">{ubo.full_name}</div>
                            {secondary && <div className="mt-0.5 text-xs text-zinc-500">{secondary}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle"><SourceBadge source={ubo.source}/></td>
                      <td className="px-4 py-3 align-middle text-xs font-mono text-zinc-500">
                        {ubo.ownership_percentage == null ? null : `${Number(ubo.ownership_percentage)}%`}
                      </td>
                      <td className="px-4 py-3 align-middle">{verificationStatus ? <StatusBadge status={verificationStatus}/> : null}</td>
                      <td className="px-4 py-3 align-middle">
                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button type="button" disabled={saving}
                                className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 border border-zinc-200 rounded-sm hover:border-zinc-400 disabled:opacity-50">
                                <MoreHorizontal className="h-3 w-3" strokeWidth={1.7}/> More <ChevronDown className="h-3 w-3" strokeWidth={1.7}/>
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-44 rounded-sm border-zinc-200 bg-white p-1 text-zinc-700 shadow-md">
                              {!required && (
                                <DropdownMenuItem disabled={!canReview || recordTerminal} onSelect={() => setUboRequired(ubo, true)}
                                  className="cursor-pointer rounded-sm px-2 py-1 text-xs text-zinc-800 hover:bg-zinc-100 focus:bg-zinc-100 focus:text-zinc-900">
                                  <PlusCircle className="h-3 w-3 text-zinc-500"/> Add verification
                                </DropdownMenuItem>
                              )}
                              {required && !linkedKycId && (
                                <>
                                  <DropdownMenuItem disabled={!canReview || recordTerminal} onSelect={() => createUboKyc(ubo)}
                                    className="cursor-pointer rounded-sm px-2 py-1 text-xs text-zinc-800 hover:bg-zinc-100 focus:bg-zinc-100 focus:text-zinc-900">
                                    <Plus className="h-3 w-3 text-zinc-500"/> Create KYC session
                                  </DropdownMenuItem>
                                  <DropdownMenuItem disabled={!canReview || recordTerminal} onSelect={() => openLinkExistingUboKyc(ubo)}
                                    className="cursor-pointer rounded-sm px-2 py-1 text-xs text-zinc-800 hover:bg-zinc-100 focus:bg-zinc-100 focus:text-zinc-900">
                                    <LinkIcon className="h-3 w-3 text-zinc-500"/> Link KYC session
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator className="bg-zinc-100"/>
                                </>
                              )}
                              {linkedKycId && (
                                <>
                                  <DropdownMenuItem asChild className="cursor-pointer rounded-sm px-2 py-1 text-xs text-zinc-800 hover:bg-zinc-100 focus:bg-zinc-100 focus:text-zinc-900">
                                    <Link to={`/dashboard/verifications/${linkedKycId}`}><Eye className="h-3 w-3 text-zinc-500"/> View KYC session</Link>
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator className="bg-zinc-100"/>
                                </>
                              )}
                              {required && (
                                <DropdownMenuItem disabled={!canReview || recordTerminal} onSelect={() => setUboRequired(ubo, false)}
                                  className="cursor-pointer rounded-sm px-2 py-1 text-xs text-zinc-800 hover:bg-zinc-100 focus:bg-zinc-100 focus:text-zinc-900">
                                  <MinusCircle className="h-3 w-3 text-zinc-500"/> Remove verification
                                </DropdownMenuItem>
                              )}
                              {linkedKycId && (
                                <DropdownMenuItem disabled={!canReview || recordTerminal} onSelect={() => unlinkUboVerification(ubo)}
                                  className="cursor-pointer rounded-sm px-2 py-1 text-xs text-zinc-800 hover:bg-zinc-100 focus:bg-zinc-100 focus:text-zinc-900">
                                  <MinusCircle className="h-3 w-3 text-zinc-500"/> Unlink KYC session
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <button type="button" onClick={() => editUbo(ubo)} disabled={!canReview || recordTerminal}
                            className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 border border-zinc-200 rounded-sm hover:border-zinc-400 disabled:opacity-50">
                            <Pencil className="h-3 w-3"/> Edit
                          </button>
                          <button type="button" onClick={() => deleteUbo(ubo)} disabled={!canReview || recordTerminal || saving}
                            className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 border border-red-200 bg-white text-red-700 rounded-sm hover:border-red-300 disabled:opacity-50">
                            <Trash2 className="h-3 w-3"/> Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      {uboModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 px-4">
          <div className="w-full max-w-lg border border-zinc-200 bg-white p-5 shadow-sm">
            <h2 className="font-display text-lg font-semibold">{editingUboId ? 'Edit UBO' : 'Add UBO'}</h2>
            <p className="mt-2 text-sm leading-6 text-zinc-600">Record a beneficial owner and optionally require an individual KYC verification.</p>
            <div className="mt-5 grid gap-4">
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Full name</span>
                <input value={uboForm.full_name} onChange={(event) => setUboField('full_name', event.target.value)}
                  onBlur={() => setUboAttempted(true)}
                  className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
                {uboAttempted && uboErrors.full_name && <span className="mt-1 block text-xs text-red-600">{uboErrors.full_name}</span>}
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Date of birth</span>
                  <input type="date" value={uboForm.date_of_birth} onChange={(event) => setUboField('date_of_birth', event.target.value)}
                    className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
                </label>
                <label className="block">
                  <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Nationality</span>
                  <input value={uboForm.nationality} onChange={(event) => setUboField('nationality', event.target.value)}
                    className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
                </label>
              </div>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Ownership percentage</span>
                <input type="number" min="0" max="100" step="0.01" value={uboForm.ownership_percentage}
                  onChange={(event) => setUboField('ownership_percentage', event.target.value)}
                  className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
                {uboAttempted && uboErrors.ownership_percentage && <span className="mt-1 block text-xs text-red-600">{uboErrors.ownership_percentage}</span>}
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Notes</span>
                <textarea value={uboForm.notes} onChange={(event) => setUboField('notes', event.target.value)}
                  className="w-full min-h-20 border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-zinc-700">
                <input type="checkbox" checked={uboForm.required_for_verification}
                  onChange={(event) => setUboField('required_for_verification', event.target.checked)}/>
                Verification required
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={closeUboModal} className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400">Cancel</button>
              <button type="button" onClick={saveUbo} disabled={saving || !uboFormValid}
                className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800 disabled:bg-zinc-100 disabled:text-zinc-400">
                {saving ? 'Saving...' : editingUboId ? 'Save UBO' : 'Add UBO'}
              </button>
            </div>
          </div>
        </div>
      )}
      {representativeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 px-4">
          <div className="w-full max-w-lg border border-zinc-200 bg-white p-5 shadow-sm">
            <h2 className="font-display text-lg font-semibold">
              {editingRepresentativeId ? 'Edit representative' : 'Add representative'}
            </h2>
            <p className="mt-2 text-sm leading-6 text-zinc-600">
              Add a director, UBO, legal representative, or authorized signer for this business verification.
            </p>

            <div className="mt-5 grid gap-4">
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">First name</span>
                <input value={representativeForm.first_name} onChange={(e) => setRepresentativeField('first_name', e.target.value)}
                  onBlur={() => setRepresentativeAttempted(true)}
                  className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
                {representativeAttempted && representativeErrors.first_name && (
                  <span className="mt-1 block text-xs text-red-600">{representativeErrors.first_name}</span>
                )}
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Last name</span>
                <input value={representativeForm.last_name} onChange={(e) => setRepresentativeField('last_name', e.target.value)}
                  onBlur={() => setRepresentativeAttempted(true)}
                  className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
                {representativeAttempted && representativeErrors.last_name && (
                  <span className="mt-1 block text-xs text-red-600">{representativeErrors.last_name}</span>
                )}
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Role / title</span>
                <input value={representativeForm.title} onChange={(e) => setRepresentativeField('title', e.target.value)}
                  onBlur={() => setRepresentativeAttempted(true)}
                  className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
                {representativeAttempted && representativeErrors.title && (
                  <span className="mt-1 block text-xs text-red-600">{representativeErrors.title}</span>
                )}
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Email</span>
                <input type="email" value={representativeForm.email} onChange={(e) => setRepresentativeField('email', e.target.value)}
                  onBlur={() => setRepresentativeAttempted(true)}
                  className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
                {(representativeAttempted || trimmedRepresentativeForm.email) && representativeErrors.email && (
                  <span className="mt-1 block text-xs text-red-600">{representativeErrors.email}</span>
                )}
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Since</span>
                <input type="date" value={representativeForm.source_started_at}
                  onChange={(e) => setRepresentativeField('source_started_at', e.target.value)}
                  className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
              </label>
              <label className="block">
                <span className="inline-flex items-center gap-2 text-sm text-zinc-700">
                  <input type="checkbox" checked={representativeForm.required_for_verification}
                    onChange={(event) => setRepresentativeField('required_for_verification', event.target.checked)}/>
                  Verification required
                </span>
                <span className="mt-1 block text-xs text-zinc-500">
                  Require this representative to complete KYC before KYB approval.
                </span>
              </label>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={resetRepresentativeForm}
                className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400">
                Cancel
              </button>
              <button type="button" onClick={saveRepresentative} disabled={saving || !representativeFormValid}
                className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800 disabled:border disabled:border-zinc-200 disabled:bg-zinc-100 disabled:text-zinc-400 disabled:hover:bg-zinc-100">
                {saving ? 'Saving...' : editingRepresentativeId ? 'Save representative' : 'Add representative'}
              </button>
            </div>
          </div>
        </div>
      )}
      {(linkKycRepresentative || linkKycUbo) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 px-4">
          <div className="w-full max-w-2xl border border-zinc-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-lg font-semibold">Link existing KYC</h2>
                <p className="mt-2 text-sm leading-6 text-zinc-600">
                  Choose an existing individual verification to link to this {linkKycUbo ? 'UBO' : 'representative'}.
                </p>
              </div>
              <button type="button" onClick={closeLinkExistingKyc}
                className="text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-800">
                Close
              </button>
            </div>

            <div className="mt-5">
              <input value={eligibleKycSearch} onChange={(event) => setEligibleKycSearch(event.target.value)}
                className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"
                placeholder="Search by name, email, or document number"/>
            </div>

            <div className="mt-4 max-h-[22rem] overflow-y-auto border border-zinc-200">
              {eligibleKycLoading ? (
                <div className="px-4 py-8 text-center text-sm text-zinc-500">Searching eligible KYC verifications...</div>
              ) : eligibleKycItems.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-zinc-500">No eligible KYC verifications found.</div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {eligibleKycItems.map((verification) => {
                    const documentType = formatDocumentType(verification.document_type);
                    const matchLabel = {
                      email_match: 'Email match',
                      name_match: 'Name match',
                      recent: 'Recent',
                    }[verification.match_reason] || 'Recent';
                    return (
                      <div key={verification.id} className="grid gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-medium text-zinc-900">{verification.applicant_name || 'Unnamed applicant'}</div>
                            <KycStatusBadge status={verification.status}/>
                            <span className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">{matchLabel}</span>
                          </div>
                          <div className="mt-1 text-xs font-mono text-zinc-500">{verification.applicant_email || 'No email'}</div>
                          {verification.document_number && (
                            <div className="mt-2 text-xs text-zinc-500">
                              <span className="font-mono">{verification.document_number}</span>
                              {documentType && <span className="ml-2">{documentType}</span>}
                            </div>
                          )}
                          <div className="mt-2 text-xs font-mono text-zinc-400">
                            Updated {fmtDate(verification.updated_at || verification.created_at)}
                          </div>
                        </div>
                        <button type="button" onClick={() => linkExistingKyc(verification)} disabled={linkingKycId === verification.id}
                          className="justify-self-start sm:justify-self-end text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400 disabled:opacity-50">
                          {linkingKycId === verification.id ? 'Linking...' : 'Link'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {pendingKycLink && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/20 px-4">
          <div className="w-full max-w-md border border-zinc-200 bg-white p-5 shadow-sm">
            <h2 className="font-display text-lg font-semibold">Link non-approved KYC?</h2>
            <p className="mt-2 text-sm leading-6 text-zinc-600">
              This KYC verification is not approved yet. You can link it, but the KYB checklist will remain incomplete until the KYC is approved.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setPendingKycLink(null)}
                className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400">
                Cancel
              </button>
              <button type="button" onClick={() => linkExistingKyc(pendingKycLink, { confirmed: true })} disabled={linkingKycId === pendingKycLink.id}
                className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50">
                Link KYC
              </button>
            </div>
          </div>
        </div>
      )}
      {verifyConfirmationOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 px-4">
          <div className="w-full max-w-md border border-zinc-200 bg-white p-5 shadow-sm">
            <h2 className="font-display text-lg font-semibold">Verify business anyway?</h2>
            <p className="mt-2 text-sm leading-6 text-zinc-600">
              This business verification still has incomplete checks. You can mark it as verified, but the decision will be recorded in the audit trail.
            </p>
            {incompleteVerificationChecks.length > 0 && (
              <ul className="mt-4 space-y-2 text-sm text-zinc-600">
                {incompleteVerificationChecks.map((check) => (
                  <li key={check} className="flex gap-2">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-zinc-400"/>
                    <span>{check}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setVerifyConfirmationOpen(false)}
                className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400">
                Cancel
              </button>
              <button type="button" onClick={confirmMarkVerified} disabled={saving}
                className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50">
                Mark verified
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 px-4">
          <div className="w-full max-w-md border border-zinc-200 bg-white p-5 shadow-sm">
            <h2 className="font-display text-lg font-semibold">
              {confirmAction.type === 'unlink' ? 'Unlink KYC verification?' : 'Remove representative?'}
            </h2>
            <p className="mt-2 text-sm leading-6 text-zinc-600">
              {confirmAction.type === 'unlink'
                ? 'This removes the link between this representative and the KYC verification. The KYC verification itself will remain available under Individuals.'
                : 'This removes the representative from this business verification. Any linked KYC verification will remain available under Individuals.'}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmAction(null)}
                className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400">
                Cancel
              </button>
              <button type="button" onClick={confirmRepresentativeAction} disabled={saving}
                className={`text-sm px-3 py-2 border rounded-sm disabled:opacity-50 ${
                  confirmAction.type === 'remove'
                    ? 'border-red-200 bg-white text-red-700 hover:border-red-300'
                    : 'border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-800'
                }`}>
                {confirmAction.type === 'unlink' ? 'Unlink' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
